import { CSSProperties } from 'react';

// カラー定義
const colors = {
  modalBg: 'rgba(20, 20, 30, 0.98)',
  modalBorder: '#4a4a6a',
  tabActive: '#6b9eff',
  tabInactive: '#3a3a5a',
  tabHover: '#5a5a7a',
  textPrimary: '#ffffff',
  textSecondary: '#a0a0c0',
  accent: '#ffd700',
  slideBg: 'rgba(40, 40, 60, 0.8)',
  overlay: 'rgba(0, 0, 0, 0.7)',
};

export const helpStyles: Record<string, CSSProperties> = {
  // オーバーレイ
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: colors.overlay,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },

  // モーダル本体
  modal: {
    backgroundColor: colors.modalBg,
    border: `2px solid ${colors.modalBorder}`,
    borderRadius: 12,
    width: '90vw',
    maxWidth: 800,
    height: '80vh',
    maxHeight: 600,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
  },

  // モーダルヘッダー
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: `1px solid ${colors.modalBorder}`,
  },

  title: {
    color: colors.textPrimary,
    fontSize: 20,
    fontWeight: 'bold',
    margin: 0,
  },

  closeButton: {
    background: 'transparent',
    border: 'none',
    color: colors.textSecondary,
    fontSize: 24,
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: 4,
    transition: 'color 0.2s',
  },

  // メインタブ
  tabContainer: {
    display: 'flex',
    gap: 8,
    padding: '12px 20px',
    borderBottom: `1px solid ${colors.modalBorder}`,
  },

  tab: {
    padding: '10px 24px',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 'bold',
    transition: 'all 0.2s',
  },

  tabActive: {
    backgroundColor: colors.tabActive,
    color: colors.textPrimary,
  },

  tabInactive: {
    backgroundColor: colors.tabInactive,
    color: colors.textSecondary,
  },

  // コンテンツエリア
  content: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },

  // クイックガイド
  quickGuideContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: 20,
    overflow: 'hidden',
  },

  slideContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },

  slide: {
    backgroundColor: colors.slideBg,
    borderRadius: 16,
    padding: 32,
    width: '100%',
    maxWidth: 500,
    textAlign: 'center',
  },

  slideIcon: {
    fontSize: 48,
    marginBottom: 16,
  },

  slideTitle: {
    color: colors.textPrimary,
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 20,
  },

  slideContent: {
    color: colors.textSecondary,
    fontSize: 16,
    lineHeight: 1.8,
    textAlign: 'left',
  },

  slidePoint: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 12,
  },

  slideBullet: {
    color: colors.accent,
    fontWeight: 'bold',
  },

  slideHighlight: {
    backgroundColor: 'rgba(107, 158, 255, 0.2)',
    color: colors.accent,
    padding: '8px 16px',
    borderRadius: 8,
    marginTop: 16,
    fontWeight: 'bold',
  },

  // ナビゲーション
  navigation: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    padding: '16px 0',
  },

  navButton: {
    backgroundColor: colors.tabInactive,
    border: 'none',
    borderRadius: 8,
    color: colors.textPrimary,
    padding: '8px 16px',
    cursor: 'pointer',
    fontSize: 18,
    transition: 'background-color 0.2s',
  },

  navButtonDisabled: {
    opacity: 0.3,
    cursor: 'not-allowed',
  },

  indicators: {
    display: 'flex',
    gap: 8,
  },

  indicator: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },

  indicatorActive: {
    backgroundColor: colors.tabActive,
  },

  indicatorInactive: {
    backgroundColor: colors.tabInactive,
  },

  // 詳細ルール
  detailedContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },

  subTabContainer: {
    display: 'flex',
    gap: 4,
    padding: '12px 20px',
    borderBottom: `1px solid ${colors.modalBorder}`,
    overflowX: 'auto',
    flexShrink: 0,
  },

  subTab: {
    padding: '8px 16px',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
    transition: 'all 0.2s',
  },

  ruleContent: {
    flex: 1,
    overflow: 'auto',
    padding: 20,
  },

  ruleSection: {
    marginBottom: 24,
  },

  ruleSectionTitle: {
    color: colors.accent,
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 12,
    paddingBottom: 8,
    borderBottom: `1px solid ${colors.modalBorder}`,
  },

  ruleText: {
    color: colors.textSecondary,
    fontSize: 14,
    lineHeight: 1.8,
    marginBottom: 8,
  },

  ruleList: {
    margin: 0,
    paddingLeft: 20,
    color: colors.textSecondary,
    fontSize: 14,
    lineHeight: 2,
  },

  ruleListItem: {
    marginBottom: 4,
  },

  ruleNote: {
    backgroundColor: 'rgba(107, 158, 255, 0.1)',
    border: `1px solid ${colors.tabActive}`,
    borderRadius: 8,
    padding: 12,
    color: colors.textSecondary,
    fontSize: 13,
    marginTop: 12,
  },

  // 用語集テーブル
  glossaryTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 14,
  },

  glossaryHeader: {
    backgroundColor: colors.tabInactive,
    color: colors.textPrimary,
    padding: '10px 12px',
    textAlign: 'left',
    fontWeight: 'bold',
  },

  glossaryCell: {
    padding: '10px 12px',
    borderBottom: `1px solid ${colors.modalBorder}`,
    color: colors.textSecondary,
  },

  glossaryTerm: {
    color: colors.accent,
    fontWeight: 'bold',
  },

  // ヘルプボタン
  helpButton: {
    backgroundColor: 'rgba(107, 158, 255, 0.2)',
    border: '1px solid rgba(107, 158, 255, 0.4)',
    borderRadius: 8,
    color: colors.textPrimary,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },

  helpButtonSmall: {
    width: 32,
    height: 32,
    fontSize: 16,
  },

  helpButtonMedium: {
    width: 40,
    height: 40,
    fontSize: 20,
  },

  helpButtonLarge: {
    padding: '8px 16px',
    fontSize: 14,
    gap: 8,
  },

  // 画像
  slideImage: {
    maxWidth: '100%',
    maxHeight: 200,
    borderRadius: 8,
    marginBottom: 16,
    border: `1px solid ${colors.modalBorder}`,
  },
};

export default helpStyles;
